/**
 * Spatial distribution validators for photogrammetry photo sets.
 *
 * These checks analyze GPS coordinates to detect coverage problems that would cause
 * holes, poor geometry, or failed reconstruction. Thresholds are derived from:
 *  - Bentley ContextCapture Acquisition Best Practices
 *  - Skyfish Photogrammetry Best Practices v3.31
 *  - RMA Communication Tower Capture Guide V16.0
 */
import { GeoCoords } from '@App/app/entities/shared/geo-coords';
import { FileMetadataInfo } from '../../../models/uploaded-image-metadata.model';
import { ValidationWarning, ValidationWarningType } from '../../../models/validation-warning.model';

/**
 * Bare minimum geotagged photo count. Real tower captures use 300-1900+, but below 50
 * the engine cannot reliably triangulate any structure. Bentley guide: >=36 photos per orbit,
 * with multiple orbits required for full coverage.
 */
const MIN_PHOTO_COUNT = 50;

/**
 * Max acceptable azimuth gap (degrees) between adjacent photo positions around the center.
 * Gaps wider than this leave surfaces unobserved from any angle, causing mesh holes.
 * Bentley guide: max 10-15° between consecutive photos; 30° is the outer warning limit.
 */
const ANGULAR_GAP_THRESHOLD = 30;

/**
 * Per-band azimuth gap (degrees) that marks an orbit ring as broken. Looser than the
 * pooled ANGULAR_GAP_THRESHOLD: a single ring carries far fewer photos than the whole
 * set, and drawing a ground circle in lat/lon makes an ellipse whose azimuths bunch up,
 * so even a complete minimum-size ring shows ~38° gaps. 60° clears that noise while still
 * catching genuinely missing arcs (a half/quarter-covered ring gaps by 180°/270°).
 */
const ORBIT_GAP_THRESHOLD = 60;

/**
 * Minimum photos in an altitude band before we judge its orbit completeness. Below roughly
 * a dozen, a band is more likely transition or top-dome shots than an intended orbit ring,
 * so we skip it rather than flag a sparse pass as a broken orbit.
 */
const ORBIT_ASSESS_MIN_PHOTOS = 12;

/**
 * Minimum number of distinct altitude bands. Tower capture requires orbits at multiple
 * heights (compound, structure, rad center, top dome). A single altitude band only covers
 * one horizontal slice of the tower.
 */
const MIN_ALTITUDE_LAYERS = 3;

// --- Azimuth & angular gap utilities --------------------------------------------

/** Bearing from one GPS coordinate to another, in degrees [0, 360). */
const calcAzimuth = (from: GeoCoords, to: GeoCoords): number => {
  const lat1 = (from.latitude * Math.PI) / 180;
  const lat2 = (to.latitude * Math.PI) / 180;
  const dLon = ((to.longitude - from.longitude) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/** Largest gap between sorted azimuths, including the wrap-around gap from last to first. */
const findMaxAngularGap = (azimuths: number[]): number => {
  if (azimuths.length < 2) return 360;
  const sorted = [...azimuths].sort((a, b) => a - b);
  let maxGap = 0;
  for (let i = 1; i < sorted.length; i++) {
    maxGap = Math.max(maxGap, sorted[i] - sorted[i - 1]);
  }
  return Math.max(maxGap, 360 - sorted[sorted.length - 1] + sorted[0]);
};

// --- Altitude layer detection ---------------------------------------------------

/**
 * Adaptive bin height for splitting altitudes into bands: max(range/10, 3m),
 * with a 3m floor to avoid over-counting from GPS altitude noise. Returns null
 * when the spread is too small (<2m) to treat as more than a single band.
 */
const altitudeBinHeight = (sortedAltitudes: number[]): number | null => {
  const range = sortedAltitudes[sortedAltitudes.length - 1] - sortedAltitudes[0];
  if (range < 2) return null;
  return Math.max(range / 10, 3);
};

/**
 * Groups photos into altitude bands by bucketing altitudes into bins of altitudeBinHeight.
 * Each band is the set of photos flown at roughly one height -- i.e. one orbit ring.
 */
const groupByAltitudeBand = (photos: FileMetadataInfo[]): FileMetadataInfo[][] => {
  const withAlt = photos
    .filter((p) => p.altitude != null)
    .sort((a, b) => (a.altitude as number) - (b.altitude as number));
  if (!withAlt.length) return [];
  const h = altitudeBinHeight(withAlt.map((p) => p.altitude as number));
  if (h === null) return [withAlt];
  const base = withAlt[0].altitude as number;
  const bands = new Map<number, FileMetadataInfo[]>();
  for (const photo of withAlt) {
    const key = Math.floor(((photo.altitude as number) - base) / h);
    const band = bands.get(key);
    if (band) band.push(photo);
    else bands.set(key, [photo]);
  }
  return [...bands.values()];
};

// --- Individual spatial checks --------------------------------------------------

/** Detects sectors of the structure that have no photo coverage. */
const checkAngularCoverage = (p: FileMetadataInfo[], c: GeoCoords): ValidationWarning | null => {
  const gap = findMaxAngularGap(p.map((f) => calcAzimuth(c, f.coords as GeoCoords)));
  if (gap <= ANGULAR_GAP_THRESHOLD) return null;
  return {
    type: ValidationWarningType.INSUFFICIENT_ANGULAR_COVERAGE,
    severity: 'warning',
    message: `Largest angular gap is ${Math.round(
      gap,
    )}°. Cover all sides (gaps < ${ANGULAR_GAP_THRESHOLD}°).`,
  };
};

/** Detects when photos only cover a narrow vertical band of the structure. */
const checkAltLayers = (photos: FileMetadataInfo[]): ValidationWarning | null => {
  const layers = groupByAltitudeBand(photos).length;
  if (layers === 0 || layers >= MIN_ALTITUDE_LAYERS) return null;
  return {
    type: ValidationWarningType.INSUFFICIENT_ALTITUDE_LAYERS,
    severity: 'warning',
    message: `Photos cover ${layers} altitude layer(s). At least ${MIN_ALTITUDE_LAYERS} recommended for full vertical coverage.`,
  };
};

/**
 * Detects altitude bands that have enough photos to be an intended orbit but whose
 * photos don't wrap all the way around the structure (a broken or missing orbit).
 *
 * The global angular-coverage check pools every altitude together, so a capture where
 * each ring covers only one side -- yet the union spans 360° -- slips through. Checking
 * the azimuth gap per altitude band catches those incomplete rings. Bentley guide:
 * each orbit must be a complete 360° circle around the structure.
 */
const checkOrbitCompleteness = (
  photos: FileMetadataInfo[],
  center: GeoCoords,
): ValidationWarning | null => {
  const bands = groupByAltitudeBand(photos);
  if (bands.length < 2) return null;
  const gaps = bands
    .filter((band) => band.length >= ORBIT_ASSESS_MIN_PHOTOS)
    .map((band) => findMaxAngularGap(band.map((f) => calcAzimuth(center, f.coords as GeoCoords))))
    .filter((gap) => gap > ORBIT_GAP_THRESHOLD);
  if (!gaps.length) return null;
  const worstGap = Math.round(Math.max(...gaps));
  return {
    type: ValidationWarningType.INCOMPLETE_ORBIT,
    severity: 'warning',
    message: `${gaps.length} altitude orbit(s) don't fully circle the structure (largest gap ${worstGap}°). Each orbit should cover all sides (gaps < ${ORBIT_GAP_THRESHOLD}°).`,
  };
};

/** Flags datasets that are too small to produce a usable model. */
const checkPhotoCount = (photos: FileMetadataInfo[]): ValidationWarning | null => {
  if (photos.length >= MIN_PHOTO_COUNT) return null;
  return {
    type: ValidationWarningType.TOO_FEW_PHOTOS,
    severity: 'warning',
    message: `Only ${photos.length} geotagged photos. At least ${MIN_PHOTO_COUNT} recommended for reliable 3D reconstruction.`,
  };
};

/** Checks GPS positions for sufficient count, angular coverage, and altitude distribution. */
export const validateSpatialDistribution = (
  photos: FileMetadataInfo[],
  center?: GeoCoords,
): ValidationWarning[] => {
  const countW = checkPhotoCount(photos);
  const spatial =
    center && photos.length >= 2
      ? [
          checkAngularCoverage(photos, center),
          checkAltLayers(photos),
          checkOrbitCompleteness(photos, center),
        ]
      : [];
  return [countW, ...spatial].filter((w): w is ValidationWarning => w !== null);
};
